import React from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import highlightKeyword from '@/components/Approval/@components/highlightKeyword';
import { resolveDepartmentName } from '@/utils/approval/ceoAffiliation';
import { Referrer } from '../../types';
import {
  Avatar,
  CheckSlot,
  DeptText,
  Identity,
  NameLine,
  NameText,
  PositionText,
  RowRoot,
} from './styles';

type P = {
  referrer: Referrer;
  /** 검색어 — 이름에서 일치 부분만 하이라이트 */
  keyword?: string;
  checked: boolean;
  focused: boolean;
  disabled?: boolean;
  onToggle: (r: Referrer) => void;
  /** 키보드 하이라이트 이동 시 scrollIntoView 대상 */
  rootRef?: React.Ref<HTMLDivElement>;
};

const initialsOf = (name: string) => {
  const trimmed = name.trim();
  if (!trimmed) return '';
  // 한글은 첫 글자, 영문은 앞 2글자.
  return /^[가-힣]/.test(trimmed)
    ? trimmed.charAt(0)
    : trimmed.slice(0, 2).toUpperCase();
};

/** 드롭다운 옵션 한 행 — focused 시 hover 와 동일 톤 배경 */
const OptionRow = ({
  referrer,
  keyword = '',
  checked,
  focused,
  disabled,
  onToggle,
  rootRef,
}: P) => {
  const { t } = useTranslation();

  const handleToggle = () => {
    if (disabled) return;
    onToggle(referrer);
  };

  const department = resolveDepartmentName(
    { passportId: referrer.id, departmentName: referrer.departmentName },
    referrer.departmentName,
  );
  const affiliation = referrer.divisionName
    ? `${referrer.divisionName} · ${department}`
    : department;

  return (
    <RowRoot
      ref={rootRef}
      role="option"
      aria-selected={checked}
      $focused={focused}
      $disabled={disabled}
      onMouseDown={(e) => {
        // input blur 이전에 onClick 이 발생하도록 기본 포커스 이동 방지.
        e.preventDefault();
      }}
      onClick={handleToggle}
    >
      <CheckSlot>
        <Checkbox
          sx={{ padding: 0 }}
          checked={checked}
          disabled={disabled}
          tabIndex={-1}
          inputProps={{
            'aria-label': t('{{name}} 선택', { name: referrer.name }),
          }}
          onClick={(e) => e.stopPropagation()}
          onChange={handleToggle}
        />
      </CheckSlot>
      <Avatar aria-hidden>
        {referrer.profileImageUrl ? (
          <img
            src={referrer.profileImageUrl}
            alt=""
            onError={(e) => {
              e.currentTarget.src = '/images/dummy/default-profile.svg';
            }}
          />
        ) : (
          initialsOf(referrer.name)
        )}
      </Avatar>
      <Identity>
        <NameLine>
          <NameText className="name">
            {highlightKeyword(referrer.name, keyword)}
          </NameText>
          {referrer.positionName && (
            <PositionText>{referrer.positionName}</PositionText>
          )}
        </NameLine>
        {department && <DeptText>{affiliation}</DeptText>}
      </Identity>
    </RowRoot>
  );
};

export default OptionRow;
